/**
 * Maps backend error messages to friendly, user-readable messages
 * with combat sports humor to soften the blow 🥊
 */

// Rotating pool of combat sports themed fallback messages
const funFallbackMessages = [
  "That move got stuffed! Let's reset and try again 🤼",
  "Whoa, got caught in a bad position. Tap and retry! 🥋",
  "The servers pulled guard. Give them a sec to recover! 🔄",
  "Technical foul! Our ref is reviewing the play... ⏱️",
  "That combo didn't land. Let's work on the setup! 🥊",
  "Swept! Back to base and try that again 🧹",
  "The code tapped out. Sending in fresh grapplers! 🤼",
  "Got heel hooked by an error. Stretching it out... 🦵",
  "That submission attempt failed. Time to drill more reps! 💪",
  "Scramble! Something went wrong but we're working back to top 🔝",
  "The triangle didn't lock. Readjusting the angle... 📐",
  "Caught in side control by a bug. Working the escape! 🐛",
  "That takedown got sprawled on. Shooting again... 🎯",
  "Round over! Take a breather and come back swinging 🔔",
];

// Get a random fun message
function getRandomFallback(): string {
  return funFallbackMessages[Math.floor(Math.random() * funFallbackMessages.length)];
}

// Known error patterns and their combat sports themed translations
const errorMappings: Array<{
  patterns: string[];
  message: string;
}> = [
  // Direct Invite errors
  {
    patterns: ['already been sent', 'already sent', 'pending invitation'],
    message: "They've already got an invite in their corner! Check their inbox 📬",
  },
  {
    patterns: ['already a member', 'existing member'],
    message: "They're already on the team! No need for another walkout 🥋",
  },
  {
    patterns: ['company not found'],
    message: "Can't find your gym! Did someone move the mats? Try refreshing 🔄",
  },
  {
    patterns: ['email is required', 'invalid email'],
    message: "Need a valid email to send the invite - no phantom fighters! 👻",
  },
  
  // Auth/Permission errors
  {
    patterns: ['not authenticated', 'unauthorized', 'not authorized'],
    message: "Session tapped out! Log back in to get on the mats 🔐",
  },
  {
    patterns: ['forbidden', 'permission denied', 'not allowed'],
    message: "That move's above your belt rank! Talk to your coach for access 🥋",
  },
  
  // Network errors
  {
    patterns: ['network', 'connection', 'timeout', 'failed to fetch'],
    message: "Lost connection to the gym! Check your internet and shoot again 📡",
  },
  {
    patterns: ['edge function', 'non-2xx'],
    message: "Servers are rolling. Reset and try that technique again! 🤼",
  },
  
  // Rate limiting
  {
    patterns: ['too many', 'rate limit', 'slow down'],
    message: "Easy there, champ! Catch your breath between rounds ⏱️",
  },
  
  // Data errors
  {
    patterns: ['not found', 'does not exist'],
    message: "That record pulled a no-show. It might've been cut from the roster 🔍",
  },
  {
    patterns: ['duplicate', 'already exists', 'unique constraint'],
    message: "Already on the card! No need for a rematch entry 🎫",
  },
];

/**
 * Convert a backend error to a friendly user message
 * @param error - The error object or message from the backend
 * @param context - Optional context for more specific messages (e.g., 'invite', 'login')
 */
export function getFriendlyErrorMessage(
  error: unknown,
  context?: 'invite' | 'login' | 'general'
): string {
  // Extract error message string
  let errorMessage = '';
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error && typeof error === 'object') {
    const errObj = error as Record<string, unknown>;
    
    // Handle Supabase FunctionsHttpError - try to get response body
    if (errObj.context && typeof errObj.context === 'object') {
      const context = errObj.context as Record<string, unknown>;
      // Try to extract error from response body
      if (context.error) {
        errorMessage = String(context.error);
      } else if (context.message) {
        errorMessage = String(context.message);
      }
    }
    
    // Fallback to standard error properties
    if (!errorMessage) {
      if (errObj.error) {
        errorMessage = String(errObj.error);
      } else if (errObj.message) {
        errorMessage = String(errObj.message);
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
    }
  }

  // Log the actual error for debugging
  console.error('[FriendlyError] Original error:', error);
  
  const lowerMessage = errorMessage.toLowerCase();
  
  // Check against known patterns
  for (const mapping of errorMappings) {
    if (mapping.patterns.some(pattern => lowerMessage.includes(pattern))) {
      return mapping.message;
    }
  }
  
  // Context-specific fallbacks
  if (context === 'invite') {
    return "That invite got blocked! Check the email and shoot again 📨";
  }
  
  if (context === 'login') {
    return "Couldn't get past the guard! Check your credentials and try again 🔑";
  }
  
  // Random combat sports fallback
  return getRandomFallback();
}

/**
 * Check if an error indicates the user should retry
 */
export function isRetryableError(error: unknown): boolean {
  const message = typeof error === 'string' ? error : 
    (error instanceof Error ? error.message : '');
  const lower = message.toLowerCase();
  
  return (
    lower.includes('network') ||
    lower.includes('timeout') ||
    lower.includes('connection') ||
    lower.includes('rate limit') ||
    lower.includes('try again')
  );
}

